/** Edit, shown but blocked (a deleted user, editing switched off on the environment): the
 *  disabled Edit button without its `E` hint — the key does nothing — that still takes the
 *  focus and says why, in its tooltip and its name.
 *
 *  ONE accessible element: the wrapper is the button screen readers meet — "Edit — <reason>",
 *  `aria-disabled`, a tab stop (a disabled `<button>` gets no pointer or keyboard focus, so
 *  it could not carry the reason) — and the visible MUI button inside is presentation only
 *  (`aria-hidden`, out of the tab order), so the control is announced once. Its focus ring
 *  is the records' ring (2 px navy `primary.main` at a 2 px offset, the theme's radius),
 *  never the browser's default outline. */
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Tooltip } from '@mui/material';

const TAB_STOP_SX = {
  // Hug the button, so the ring follows its outline rather than the line box.
  display: 'inline-flex',
  borderRadius: 1,
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
} as const;

export function BlockedEditButton({ reason }: { reason: string }) {
  return (
    <Tooltip title={reason}>
      <Box
        component="span"
        role="button"
        tabIndex={0}
        aria-disabled="true"
        aria-label={`Edit — ${reason}`}
        data-edit-blocked
        sx={TAB_STOP_SX}
      >
        <Button variant="contained" size="small" startIcon={<EditOutlinedIcon />} disabled tabIndex={-1} aria-hidden>
          Edit
        </Button>
      </Box>
    </Tooltip>
  );
}
